import { useState } from 'react'
// import dayjs from 'dayjs'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import { ArrowBack, Cancel, Close } from '@mui/icons-material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { AssignmentIcon, ConfirmIcon, UserIcon } from 'src/v3/core/icons'
import { LightweightUser } from 'src/v3/manage/manage.slice'
import { useLinkedServiceName } from '../../hooks/use-linked-service-name'
import { dataGridSx } from '../../styles'
import {
  ComplianceFormData,
  formatCredentialType,
  formatLicenseType,
  formatRecertCycleItemType,
} from '../types'

type Props = {
  formData: ComplianceFormData
  users?: LightweightUser[]
  onCancel: () => void
  onBack: () => void
  onSubmit: () => void
  isSubmitting: boolean
  submitLabel?: string
}

let userGridColumns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
  { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
]

export default function ComplianceReviewStep({
  formData,
  users = [],
  onCancel,
  onBack,
  onSubmit,
  isSubmitting,
  submitLabel = 'Create Requirement',
}: Props) {
  let linkedServiceName = useLinkedServiceName(formData.linkedServiceID)
  let [usersModalOpen, setUsersModalOpen] = useState(false)
  let [requirementsModalOpen, setRequirementsModalOpen] = useState(false)

  let requirementsCount =
    formData.licenseItems.length +
    formData.credentialItems.length +
    formData.nremtItems.length +
    formData.recertItems.length

  let selectedUsers = users.filter((u) => formData.userIDs.includes(u.id))

  let userRows = selectedUsers.map((u) => ({
    id: u.id,
    name: `${u.firstName} ${u.lastName}`,
    email: u.primaryEmail,
  }))

  return (
    <>
      <Card className="p-6 mb-6">
        <Typography variant="h6" component="h2" className="mb-4">
          Review Requirement
        </Typography>

        <Box className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
            <Typography variant="h6" className="mb-4">
              Details
            </Typography>
            <Box className="flex flex-col gap-4">
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Name:
                </Typography>
                <Typography variant="body1">{formData.name}</Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Linked Service:
                </Typography>
                <Typography variant="body1">
                  {linkedServiceName || '—'}
                </Typography>
              </Box>
              {/* Issue/expiration dates are hidden while requirement dates are phased out.
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Issue Date:
                </Typography>
                <Typography variant="body1">
                  {formData.issueDate
                    ? dayjs(formData.issueDate).format('MMMM D, YYYY')
                    : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Expiration Date:
                </Typography>
                <Typography variant="body1">
                  {formData.expirationDate
                    ? dayjs(formData.expirationDate).format('MMMM D, YYYY')
                    : '—'}
                </Typography>
              </Box>
              */}
            </Box>
          </Card>

          <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
            <Typography variant="h6" className="mb-4">
              Alert Settings
            </Typography>
            <Box className="flex flex-col gap-4">
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Alert Setting:
                </Typography>
                <Typography variant="body1">{formData.alertSetting}</Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className="mb-1"
                >
                  Alert Type:
                </Typography>
                <Typography variant="body1">{formData.alertType}</Typography>
              </Box>
            </Box>
          </Card>

          <Card sx={{ bgcolor: 'background.paperDark' }} className="p-6">
            <Typography variant="h6" className="mb-4">
              Summary
            </Typography>
            <Box className="flex flex-col gap-3">
              <Box className="flex items-center gap-2">
                <UserIcon sx={{ color: 'primary.main' }} />
                <Chip
                  label={`${formData.userIDs.length} user${formData.userIDs.length !== 1 ? 's' : ''} selected`}
                  color="primary"
                  variant="outlined"
                  onClick={() => setUsersModalOpen(true)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                />
              </Box>
              <Box className="flex items-center gap-2">
                <AssignmentIcon sx={{ color: 'primary.main' }} />
                <Chip
                  label={`${requirementsCount} requirement${requirementsCount !== 1 ? 's' : ''}`}
                  color="primary"
                  variant="outlined"
                  onClick={() => setRequirementsModalOpen(true)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.light' },
                  }}
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </Card>

      {isSubmitting && (
        <Box className="mb-6">
          <LinearProgress />
          <Typography
            variant="body2"
            color="text.secondary"
            className="mt-2 text-center"
          >
            Saving...
          </Typography>
        </Box>
      )}

      <Box className="flex justify-end gap-3">
        <Button
          variant="outlined"
          size="large"
          startIcon={<Cancel />}
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          size="large"
          startIcon={<ArrowBack />}
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<ConfirmIcon />}
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {submitLabel}
        </Button>
      </Box>

      <Dialog
        open={usersModalOpen}
        onClose={() => setUsersModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box className="flex items-center justify-between">
            <Typography variant="h6">
              Selected Users ({formData.userIDs.length})
            </Typography>
            <IconButton
              onClick={() => setUsersModalOpen(false)}
              aria-label="Close"
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <DataGridPremium
            rows={userRows}
            columns={userGridColumns}
            autoHeight
            disableRowSelectionOnClick
            hideFooter={userRows.length <= 100}
            sx={dataGridSx}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={requirementsModalOpen}
        onClose={() => setRequirementsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box className="flex items-center justify-between">
            <Typography variant="h6">
              Requirements ({requirementsCount})
            </Typography>
            <IconButton
              onClick={() => setRequirementsModalOpen(false)}
              aria-label="Close"
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {requirementsCount > 0 ? (
            <List>
              {formData.licenseItems.map((item) => (
                <ListItem key={item.id}>
                  <Chip label="License" size="small" className="mr-2" />
                  <ListItemText
                    primary={`${formatLicenseType(item.type)} - ${item.state}`}
                  />
                </ListItem>
              ))}
              {formData.credentialItems.map((item) => (
                <ListItem key={item.id}>
                  <Chip label="Credential" size="small" className="mr-2" />
                  <ListItemText primary={formatCredentialType(item)} />
                </ListItem>
              ))}
              {formData.nremtItems.map((item) => (
                <ListItem key={item.id}>
                  <Chip label="NREMT" size="small" className="mr-2" />
                  <ListItemText primary="NREMT" />
                </ListItem>
              ))}
              {formData.recertItems.map((item) => (
                <ListItem key={item.id}>
                  <Chip label="Recertification" size="small" className="mr-2" />
                  <ListItemText primary={formatRecertCycleItemType(item)} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary" className="p-4">
              No requirements have been added.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
